import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  FormControlLabel,
  FormLabel,
  Typography,
  Button,
  TextField,
  InputAdornment,
  LinearProgress,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { useDropzone } from "react-dropzone";
import LinkIcon from "@mui/icons-material/Link";
import { useSelector, useDispatch } from "react-redux";
import uploadIcon from "../../assets/upload.png";
import Thumbnail from "../../assets/Thumbnail.png";
import { darkTheme, lightTheme } from "../../theme/Theme";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import useThemeRender from "../../theme/useThemeRender";
import { showErrorToast } from "../../utils/Toast/toast";
import {
  setDisableNextButton,
  setFileUploaded,
  setFileUploadPageName,
  setInternalFileDocument,
} from "../../slices/adminPageSlice.js";
import { setImage } from "./../../slices/imageSlice.js";
import { Card, Radio } from "pepsico-ds";
import {
  deleteCuratorFile,
  uploadYearlyReviewArtificatsFiles,
} from "../../API/curatorApi.js";
import { uploadThumbnailAndGiveFileId } from "../../utils/function.js";

const StepOneForm = ({ internalData, setInternalData, setFileType }) => {
  const [contentType, setContentType] = useState("DOCUMENT");
  const [isUploadingDocument, setUploadingDocument] = useState(false);
  const [isUploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const thumbnailInputRef = useRef();
  const { fileUploaded, internalFileDocument } = useSelector(
    (store) => store.adminpage
  );
  const { flagUser } = useSelector((store) => store.user);
  const isMobile = useMediaQuery("(max-width:600px)");
  const thumbnail = useSelector((state) => state.images.images["thumbnail"]);
  const dispatch = useDispatch();
  const handlePreview = () => {
    if (internalFileDocument.file) {
      console.log(internalFileDocument.type);
      try {
        if (internalFileDocument.type === "application/pdf") {
          const fileURL = URL.createObjectURL(internalFileDocument.file);
          window.open(fileURL, "_blank");
        }
      } catch (error) {
        console.error("Error opening PDF:", error);
      }
    }
  };
  useEffect(() => {
    if (contentType === "DOCUMENT") {
      if (
        !internalFileDocument ||
        Object?.keys(internalFileDocument)?.length === 0
      ) {
        dispatch(setFileUploaded(false));
        dispatch(setInternalFileDocument(null));
      } else {
        dispatch(setDisableNextButton(false));
        dispatch(setFileUploaded(true));
        dispatch(setFileUploadPageName("external"));
      }
    } else if (contentType === "LINK") {
      if (
        internalData?.attachmentLinkUrl &&
        internalData?.attachmentLinkUrl?.trim() !== ""
      ) {
        dispatch(setDisableNextButton(false));
      } else {
        dispatch(setDisableNextButton(true));
      }
    }

    console.log(internalData, "contentType");
  }, [contentType]);
  const isDarkTheme = useSelector((state) => state.theme);
  const handleLinkChange = (e) => {
    const newLink = e.target.value;
    setInternalData((prevData) => ({
      ...prevData,
      attachmentLinkUrl: newLink,
      attachmentFileId: 0,
    }));
    if (newLink) {
      dispatch(setDisableNextButton(false));
    } else {
      dispatch(setDisableNextButton(true));
    }
  };
  const getThemeColor = (lightColor, darkColor) =>
    isDarkTheme ? darkColor : lightColor;

  const handleChange = (val) => {
    setContentType(val === "LINK" ? "LINK" : "DOCUMENT");
    setInternalData((prevData) => ({
      ...prevData,
      contentTypeId: val,
    }));
  };

  const onDrop = async (acceptedFiles) => {
    const MAX_FILE_SIZE_MB = flagUser?.maxFileSize ?? 250;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const file = acceptedFiles[0];
    if (!file) return;

    if (file?.size > MAX_FILE_SIZE_BYTES) {
      showErrorToast(
        `File size exceeds the ${MAX_FILE_SIZE_MB} MB limit. Please upload a smaller file.`
      );
      return;
    }
    setUploadingDocument(true);

    try {
      // Step 1: Initiate upload
      const isVideo = file.type.startsWith("video/");
      setFileType(isVideo ? "VIDEO" : "DOCUMENT");
      const { data } = await uploadYearlyReviewArtificatsFiles(
        acceptedFiles,
        false,
        flagUser?.maxFileSize
      );
      const fileId = data.fileId;
      dispatch(
        setInternalFileDocument({
          name: file?.name,
          type: file?.type,
          file: file,
          fileId: fileId,
        })
      );
      setInternalData((prevData) => ({
        ...prevData,
        attachmentFileId: parseInt(fileId),
        attachmentLinkUrl: "",
      }));
      dispatch(setDisableNextButton(false));
      dispatch(setFileUploaded(true));
      dispatch(setFileUploadPageName("external"));
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setUploadingDocument(false);
    }
  };
  useEffect(() => {
    if (fileUploaded) {
      dispatch(setDisableNextButton(false));
    } else {
      dispatch(setDisableNextButton(true));
    }
    console.log(fileUploaded, "fileUploadedfileUploaded");
  }, [fileUploaded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => {
      void onDrop(files); // ✅ Sonar-safe
    },
    "application/pdf": [],
    "video/*": [],
  });

  const handleThumbnailUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingThumbnail(true);

    try {
      const fileIds = await uploadThumbnailAndGiveFileId([file]);
      const fileUrl = URL.createObjectURL(file);
      dispatch(setImage({ id: "thumbnail", url: fileUrl }));
      setInternalData((prevData) => ({
        ...prevData,
        thumbnailFileId: fileIds[0],
      }));
    } catch (error) {
      // Handle error cases
      console.error(
        "Error uploading file:",
        error.response?.data || error.message
      );
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleDocumentDelete = async (fileId) => {
    if (!fileId) {
      console.error("File ID is required to delete the file.");
      return;
    }

    try {
      deleteCuratorFile(fileId);

      dispatch(setFileUploaded(false));
      dispatch(setInternalFileDocument(null));
    } catch (error) {
      console.error("Error deleting the file:", error);
    }
  };

  const renderProgressBar = () => (
    <Box sx={{ width: "100%", mt: 2 }}>
      <LinearProgress />
    </Box>
  );

  const renderDropzone = (label) => {
    const handleDragOver = (event) => {
      event.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = () => {
      setIsDragging(false);
    };

    const handleDrop = (event) => {
      event.preventDefault();
      setIsDragging(false);

      const file = event.dataTransfer.files[0]; // Get the first dropped file
      if (file) {
        handleThumbnailUpload({ target: { files: [file] } }); // Simulate file input change event
      }
    };

    return (
      <Box>
        <FormLabel
          sx={{
            color: getThemeColor(
              lightTheme.textColorLayer1,
              darkTheme.textColorLayer1
            ),
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "18px",
            fontFamily: "inherit",
          }}
        >
          {label}
        </FormLabel>
        <Box
          sx={{
            display: "flex",
            mt: 2,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
            border: `1px dashed ${isDragging ? "#005CBC" : "#D7E5F8"}`,
            padding: "24px",
            width: { xs: "100%", sm: "50%" },
            background: getThemeColor(
              lightTheme.insideCardBackgroundColorLayer3,
              darkTheme.insideCardBackgroundColorLayer3
            ),
            position: "relative",
            transition: "border 0.3s ease-in-out",
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isUploadingThumbnail ? (
            renderProgressBar()
          ) : thumbnail ? (
            <>
              <img
                src={thumbnail}
                alt="Uploaded Thumbnail"
                style={{
                  maxWidth: "100%",
                  borderRadius: "4px",
                  objectFit: "cover",
                }}
              />
              <Button
                variant="contained"
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "45%",
                  borderRadius: "24px",
                  fontWeight: 700,
                }}
                startIcon={<AutorenewOutlinedIcon />}
                onClick={() => thumbnailInputRef.current.click()}
              >
                Replace
              </Button>
              <input
                type="file"
                accept="image/*"
                ref={thumbnailInputRef}
                style={{ display: "none" }}
                onChange={handleThumbnailUpload}
              />
            </>
          ) : (
            <>
              <img src={Thumbnail} alt="Thumbnail Icon" />
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: getThemeColor(
                    lightTheme.textColorLayer1,
                    darkTheme.textColorLayer1
                  ),
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "18px",
                  fontFamily: "inherit",
                }}
              >
                {isDragging
                  ? "Drop your file here!"
                  : "Drag and drop your file here (only .jpg, .png)"}
              </Typography>
              <Button
                variant="text"
                sx={{
                  mt: 2,
                  borderRadius: "24px",
                  border: "2px solid #005CBC",
                  fontWeight: 700,
                  py: 1,
                  px: 2,
                  fontSize: "12px",
                  lineHeight: "18px",
                  fontFamily: "inherit",
                }}
                startIcon={<UploadOutlinedIcon />}
                onClick={() => thumbnailInputRef.current.click()}
              >
                Upload File
              </Button>
              <input
                type="file"
                accept="image/*"
                ref={thumbnailInputRef}
                style={{ display: "none" }}
                onChange={handleThumbnailUpload}
              />
            </>
          )}
        </Box>
      </Box>
    );
  };

  const dropzoneStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "8px",
    border: "1px dashed #D7E5F8",
    padding: "24px",
    background: useThemeRender("insideCardBackgroundColorLayer3"),
    width: { xs: "100%", sm: "50%" },
  };

  return (
    <Card
      ellipsis={false}
      isDark={isDarkTheme}
      icon=""
      title="Step 1: Upload"
      style={{ borderRadius: "8px" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "8px",
        }}
      >
        <FormLabel
          sx={{
            color: useThemeRender("textColorLayer1"),
            fontSize: "12px",
            lineHeight: "18px",
            fontFamily: "inherit",
          }}
        >
          Content type
        </FormLabel>
        <Radio
          items={["Upload", "Link"]}
          orientation="horizontal"
          reference="radio button"
          showText
          size="small"
          onUpdate={(index, value) => handleChange(value?.toLocaleUpperCase())}
          selectedItemIndex={contentType ? (contentType === "LINK" ? 1 : 0) : 0}
          isDark={isDarkTheme}
          // style={{margin: '8px 0'}}
        />

        {contentType === "LINK" && (
          <>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 400,
                mt: 1,
                mb: 0.5,
                fontFamily: "var(--Typography-Font-Family-Body, Inter)",
                //color: isDarkTheme && darkTheme.textColorLayer1,
              }}
            >
              Link
            </Typography>
            <TextField
              placeholder="Paste Link"
              sx={{
                width: isMobile ? "100%" : "50%",
                "& .MuiOutlinedInput-input": {
                  padding: "0px 8px",
                  lineHeight: "20px",
                  fontSize: "14px",
                  height: "32px",
                },
              }}
              value={internalData.attachmentLinkUrl}
              onChange={handleLinkChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <LinkIcon sx={{ fontSize: "16px" }} />
                  </InputAdornment>
                ),
              }}
            />
          </>
        )}

        {contentType === "DOCUMENT" && (
          <>
            <Box>
              {!internalFileDocument ? (
                <>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    sx={{ width: { xs: "100%", sm: "50%" } }}
                  >
                    <FormLabel
                      sx={{
                        color: getThemeColor(
                          lightTheme.textColorLayer1,
                          darkTheme.textColorLayer1
                        ),
                        fontSize: "12px",
                        lineHeight: "18px",
                        fontFamily: "inherit",
                      }}
                    >
                      Document
                    </FormLabel>
                    <Box display="flex" justifyContent="space-between">
                      <FormLabel
                        sx={{
                          color: getThemeColor(
                            lightTheme.textColorLayer4,
                            darkTheme.textColorLayer4
                          ),
                          fontSize: "10px",
                          fontFamily: "inherit",
                        }}
                      >
                        *Required
                      </FormLabel>
                    </Box>
                  </Box>
                  <Box
                    {...getRootProps()}
                    sx={{ mt: 2, mb: 2, ...dropzoneStyle }}
                  >
                    {isUploadingDocument ? (
                      renderProgressBar()
                    ) : (
                      <img src={uploadIcon} alt="Upload Icon" />
                    )}
                    <input {...getInputProps()} />
                    <Typography
                      sx={{
                        color: getThemeColor(
                          lightTheme.textColorLayer1,
                          darkTheme.textColorLayer1
                        ),
                        fontSize: "12px",
                        fontWeight: 400,
                        lineHeight: "18px",
                        fontFamily: "inherit",
                      }}
                    >
                      Drag and drop your file here
                    </Typography>
                    <Button
                      variant="text"
                      sx={{
                        mt: 2,
                        borderRadius: "24px",
                        border: "2px solid #005CBC",
                        fontWeight: 700,
                        py: 1,
                        px: 2,
                        fontSize: "12px",
                        lineHeight: "18px",
                        fontFamily: "inherit",
                      }}
                      startIcon={<UploadOutlinedIcon />}
                    >
                      Upload File
                    </Button>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: { xs: "100%", sm: "50%" },
                    height: { sm: "32px" },
                    background: getThemeColor(
                      lightTheme.insideCardBackgroundColorLayer3,
                      darkTheme.insideCardBackgroundColorLayer3
                    ),
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={handlePreview}
                  >
                    <InsertDriveFileOutlinedIcon
                      fontSize="small"
                      sx={{ marginRight: 1, color: "primary.main" }}
                    />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontFamily: "inherit",
                        lineHeight: "18px",
                      }}
                    >
                      {internalFileDocument?.name}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() =>
                      handleDocumentDelete(internalFileDocument?.fileId)
                    }
                  >
                    <DeleteOutlineOutlinedIcon
                      fontSize="small"
                      color="primary"
                    />
                  </IconButton>
                </Box>
              )}
            </Box>
            {renderDropzone("Thumbnail upload")}
          </>
        )}

        {contentType === "LINK" && renderDropzone("Thumbnail upload")}
      </Box>
    </Card>
  );
};

export default StepOneForm;
